import { PerformanceLevelView, ReportCardSection } from "../../types";

/**
 * Seven hues at one lightness and chroma, anchored on the app's #3cddc7
 * primary. Hue says WHICH criterion, never how well it scored — the grade is
 * where the mark sits on the shared axis.
 */
export const CRITERION_COLORS = [
  "#17c5b0",
  "#8da5fd",
  "#ed86a5",
  "#c9a73a",
  "#32bbea",
  "#cb90e1",
  "#ed8f61",
];

/** Cycles rather than running off the end — rubrics may define more than seven criteria. */
export function colorForIndex(index: number): string {
  return CRITERION_COLORS[index % CRITERION_COLORS.length];
}

export function groupByKind(sections: ReportCardSection[]): {
  conversation: ReportCardSection[];
  solution: ReportCardSection[];
} {
  return {
    conversation: sections.filter((s) => s.kind === "conversation"),
    solution: sections.filter((s) => s.kind === "solution"),
  };
}

/**
 * Shares of the group's total weight. Normalisation is WITHIN a kind:
 * conversation and solution are weighted separately and never combined.
 */
export function normalisedWeights(group: ReportCardSection[]): number[] {
  if (!group.length) return [];
  const total = group.reduce((sum, s) => sum + Math.max(0, s.weight), 0);
  if (total <= 0) return group.map(() => 1 / group.length);
  return group.map((s) => Math.max(0, s.weight) / total);
}

export function weightedGrade(group: ReportCardSection[]): number | null {
  if (!group.length) return null;
  const weights = normalisedWeights(group);
  return group.reduce((sum, s, i) => sum + s.grade * weights[i], 0);
}

/**
 * The band a grade falls in. Levels are inclusive at both ends, but authors
 * write them as whole numbers — 0–49, 50–69 — which leaves a one-unit hole
 * between every pair. Grades are not whole numbers: every weighted rollup is
 * fractional, and a criterion may be graded 49.5. A literal containment test
 * returns null for those and the band label silently disappears from a card
 * that has a perfectly good grade.
 *
 * So a grade that no level contains is resolved against the same boundaries the
 * axis prints and the chart's arcs are drawn at — the midpoint of each hole.
 * Below the boundary belongs to the lower band, at or above it to the higher,
 * which is what the drawn threshold already claims.
 *
 * Null is reserved for the one case that genuinely has no answer: no levels.
 */
export function bandFor(
  levels: PerformanceLevelView[],
  grade: number,
): PerformanceLevelView | null {
  const contained = levels.find(
    (l) => grade >= l.minGrade && grade <= l.maxGrade,
  );
  if (contained) return contained;

  const sorted = sortLevels(levels);
  if (!sorted.length) return null;

  const boundaries = bandBoundaries(levels);
  for (let i = 0; i < boundaries.length; i++) {
    if (grade < boundaries[i]) return sorted[i];
  }
  // Above every boundary, or below the lowest floor: pin to the end it passed.
  return grade < sorted[0].minGrade ? sorted[0] : sorted[sorted.length - 1];
}

function sortLevels(levels: PerformanceLevelView[]): PerformanceLevelView[] {
  return levels.slice().sort((a, b) => a.minGrade - b.minGrade);
}

/**
 * The band axis the mockup draws is shared across a kind, but performance
 * levels are authored per criterion — an author may give two criteria in the
 * same kind different names, counts, or thresholds. Return the common levels
 * when they agree so the axis can be drawn, and null when they do not so the
 * page can drop the strip rather than draw a scale that lies about half its
 * criteria.
 */
export function sharedBands(
  group: ReportCardSection[],
): PerformanceLevelView[] | null {
  if (!group.length) return null;
  const first = sortLevels(group[0].performanceLevels);
  if (!first.length) return null;

  for (const section of group.slice(1)) {
    const levels = sortLevels(section.performanceLevels);
    if (levels.length !== first.length) return null;
    for (let i = 0; i < levels.length; i++) {
      if (
        levels[i].name !== first[i].name ||
        levels[i].minGrade !== first[i].minGrade ||
        levels[i].maxGrade !== first[i].maxGrade
      ) {
        return null;
      }
    }
  }
  return first;
}

/**
 * The report card stores one summary. The page renders its first sentence as
 * the headline and the remainder as the paragraph beneath.
 */
export function splitSummary(summary: string | null): {
  headline: string;
  body: string;
} {
  const text = summary?.trim();
  if (!text) return { headline: "", body: "" };
  const match = /^[\s\S]*?[.!?](?=\s|$)/.exec(text);
  if (!match) return { headline: text, body: "" };
  return {
    headline: match[0].trim(),
    body: text.slice(match[0].length).trim(),
  };
}

/* ── rose chart geometry ────────────────────────────────────────────────
   The chart is drawn into a 260-unit square and scaled by CSS, so these are
   viewBox units rather than pixels. */

export const ROSE_VIEWBOX = 300;
export const ROSE_CENTER_X = 150;
export const ROSE_CENTER_Y = 150;
/**
 * The hub the slices grow out of; nothing is drawn inside it. Sized against the
 * band label rather than the chart: at the plot's usual width this is a ~49px
 * radius, whose chord at the label's own line is about 85px across — a ten
 * character band name plus a few pixels of air at each end.
 */
export const ROSE_R_INNER = 54;
/** Where a grade of 100 lands. */
export const ROSE_R_OUTER = 140;
/** Clear angle, in radians, held between neighbouring slices. */
export const SLICE_PAD = 0.042;
/** Distance a slice lifts away from the hub while it is active. */
export const SLICE_LIFT = 6;
/**
 * Radius of the circle the entrance wipe clips against. It has to clear the
 * outermost thing ever painted — the rim arc of a lifted slice, plus its own
 * stroke — or it silently truncates the chart instead of only revealing it.
 * Exported so the keyframes can be driven from it rather than restating it.
 */
export const ROSE_R_CLIP = ROSE_R_OUTER + SLICE_LIFT + 4;

/** Grades run 0–100, so anything outside that is pinned to the rim it passed. */
export function sliceRadius(grade: number): number {
  const clamped = Math.min(100, Math.max(0, grade));
  return ROSE_R_INNER + ((ROSE_R_OUTER - ROSE_R_INNER) * clamped) / 100;
}

function fixed(n: number): string {
  return n.toFixed(2);
}

/**
 * An annular sector: the rose chart's slice. Angles are in radians, measured
 * the way SVG measures them — zero points along the positive x axis and the
 * angle grows clockwise, because y grows downward.
 */
export function sectorPath(
  cx: number,
  cy: number,
  rInner: number,
  rOuter: number,
  startRad: number,
  endRad: number,
): string {
  const point = (r: number, a: number): string =>
    `${fixed(cx + r * Math.cos(a))} ${fixed(cy + r * Math.sin(a))}`;
  const large = endRad - startRad > Math.PI ? 1 : 0;
  return (
    `M${point(rOuter, startRad)}` +
    `A${fixed(rOuter)} ${fixed(rOuter)} 0 ${large} 1 ${point(rOuter, endRad)}` +
    `L${point(rInner, endRad)}` +
    `A${fixed(rInner)} ${fixed(rInner)} 0 ${large} 0 ${point(rInner, startRad)}` +
    "Z"
  );
}

/**
 * A bare arc at one radius, swept between two angles. Used for the band
 * thresholds drawn across a single slice: performance levels are authored per
 * criterion, so a threshold is only true of the wedge it is drawn over and must
 * never be closed into a ring spanning its neighbours.
 */
export function arcPath(
  cx: number,
  cy: number,
  r: number,
  startRad: number,
  endRad: number,
): string {
  const point = (a: number): string =>
    `${fixed(cx + r * Math.cos(a))} ${fixed(cy + r * Math.sin(a))}`;
  const large = endRad - startRad > Math.PI ? 1 : 0;
  return `M${point(startRad)}A${fixed(r)} ${fixed(r)} 0 ${large} 1 ${point(endRad)}`;
}

/**
 * The radii of the threshold arcs drawn across one slice: every boundary
 * between that criterion's own bands, then the 100 rim that closes the scale.
 * The rim is drawn like any other threshold because it is one — it is the
 * ceiling the top band runs up to — and it also gives the chart the outer
 * circumference the slices are measured against.
 *
 * Ordered inward to outward; the rim is always last.
 */
export function bandArcRadii(levels: PerformanceLevelView[]): number[] {
  return [...bandBoundaries(levels).map(sliceRadius), ROSE_R_OUTER];
}

export interface SliceLayout {
  section: ReportCardSection;
  /** Share of this kind's total weight, 0–1. */
  weight: number;
  startAngle: number;
  endAngle: number;
  midAngle: number;
  /** Translation applied while the slice is active, in viewBox units. */
  tx: number;
  ty: number;
  colorIndex: number;
  color: string;
}

/**
 * Lays a kind's criteria round the hub: sweep is the criterion's normalised
 * weight, and the run of colours continues from `colorOffset` so the two
 * charts on the page never repeat a hue.
 */
export function sliceLayout(
  group: ReportCardSection[],
  colorOffset = 0,
): SliceLayout[] {
  const weights = normalisedWeights(group);
  let cursor = -Math.PI / 2;
  return group.map((section, i) => {
    const sweep = weights[i] * Math.PI * 2;
    const startAngle = cursor + SLICE_PAD / 2;
    // A sweep narrower than the padding would put the end BEHIND the start, and
    // sectorPath would draw the long way round — one hairline criterion painting
    // a near-complete ring over every other slice. Collapse it to nothing
    // instead: a criterion weighted that lightly has no width to show.
    const endAngle = Math.max(startAngle, cursor + sweep - SLICE_PAD / 2);
    const midAngle = (startAngle + endAngle) / 2;
    cursor += sweep;
    return {
      section,
      weight: weights[i],
      startAngle,
      endAngle,
      midAngle,
      tx: Math.cos(midAngle) * SLICE_LIFT,
      ty: Math.sin(midAngle) * SLICE_LIFT,
      colorIndex: colorOffset + i,
      color: colorForIndex(colorOffset + i),
    };
  });
}

/** What the readout bar shows while a slice is hovered or focused. */
export interface ActiveSlice {
  criterionId: string;
  name: string;
  grade: number;
  bandName: string | null;
  weightPercent: number;
}

/* ── the shared 0–100 axis ──────────────────────────────────────────────
   Grades are a 0–100 scale everywhere in the product, so the axis spans that
   range whatever floor and ceiling the rubric's own levels happen to declare.
   Positions below are percentages of it. */

const AXIS_MIN = 0;
const AXIS_MAX = 100;

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * Where one band gives way to the next. Levels are inclusive at both ends and
 * an author may leave a gap between them, so the line falls halfway between the
 * lower band's ceiling and the higher band's floor.
 */
export function bandBoundaries(levels: PerformanceLevelView[]): number[] {
  const sorted = sortLevels(levels);
  const out: number[] = [];
  for (let i = 0; i < sorted.length - 1; i++) {
    out.push(round2((sorted[i].maxGrade + sorted[i + 1].minGrade) / 2));
  }
  return out;
}

/** The numbers printed along the axis: the ends, plus each band's floor. */
export function bandTicks(
  levels: PerformanceLevelView[],
): { value: number; position: number }[] {
  const sorted = sortLevels(levels);
  const boundaries = bandBoundaries(levels);
  return [
    { value: AXIS_MIN, position: AXIS_MIN },
    ...boundaries.map((position, i) => ({
      value: sorted[i + 1].minGrade,
      position,
    })),
    { value: AXIS_MAX, position: AXIS_MAX },
  ];
}

/**
 * The ruler drawn when the kind's criteria do not agree on a band scheme. The
 * 0–100 scale is universal — only the band names and their thresholds are
 * per-criterion — so the strip keeps its numbers and drops only what it can no
 * longer say for every row beneath it.
 */
export function plainTicks(): { value: number; position: number }[] {
  return [0, 25, 50, 75, 100].map((value) => ({ value, position: value }));
}

/** The axis numbers: the rubric's own thresholds when shared, else the ruler. */
export function axisTicks(
  levels: PerformanceLevelView[] | null,
): { value: number; position: number }[] {
  return levels && levels.length ? bandTicks(levels) : plainTicks();
}

/** Each band's stretch of the axis, and where to centre its name over it. */
export function bandSegments(
  levels: PerformanceLevelView[],
): { name: string; start: number; end: number; center: number }[] {
  const sorted = sortLevels(levels);
  if (!sorted.length) return [];
  const edges = [AXIS_MIN, ...bandBoundaries(levels), AXIS_MAX];
  return sorted.map((level, i) => ({
    name: level.name,
    start: edges[i],
    end: edges[i + 1],
    center: round2((edges[i] + edges[i + 1]) / 2),
  }));
}

/**
 * The tinted ground behind a criterion's mark: one step per band with a
 * hairline on each boundary, so the bar is read against the same scale the axis
 * above it prints. Painted inline because the boundaries belong to the rubric.
 */
export function trackGradient(boundaries: number[]): string {
  // Four tints, and the steps must never wrap: a modulo would paint a fifth
  // band in the first band's tint, so the best performance level on the card
  // would read as the worst. A rubric with more than four levels flattens at
  // the top instead — the last tint repeats, which understates a distinction
  // rather than inverting one.
  const tint = (step: number): string => `var(--t${Math.min(step, 4)})`;
  const stops: string[] = [];
  let from = "0";
  boundaries.forEach((b, i) => {
    stops.push(`${tint(i + 1)} ${from} ${b}%`);
    stops.push(`var(--tick) ${b}% calc(${b}% + 1px)`);
    from = `calc(${b}% + 1px)`;
  });
  stops.push(`${tint(boundaries.length + 1)} ${from} 100%`);
  return `linear-gradient(90deg, ${stops.join(", ")})`;
}

/**
 * Bar thickness carries the criterion's weight, so a 20%-weighted criterion
 * reads lighter than a 40%-weighted one at the same grade. Bounded so it stays
 * inside the 24px track however lopsided the rubric is.
 */
export function markHeight(weightShare: number): number {
  return Math.min(20, Math.max(6, 1 + weightShare * 100 * 0.4));
}

/**
 * The single lowest-graded criterion on the card — the one the gauge annotates
 * in the pencil tint. Ties keep the first, and an empty card flags nothing.
 */
export function weakestCriterionId(sections: ReportCardSection[]): string | null {
  let weakest: ReportCardSection | null = null;
  for (const section of sections) {
    if (!weakest || section.grade < weakest.grade) weakest = section;
  }
  return weakest?.criterionId ?? null;
}
